import { Reducer, useReducer } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Button,
} from "@mui/material";

import { useUserTerms } from "../../hooks/useUserTerms";
import { IBreachDetails } from "../../models/breach-details.interface";
import { SelectPayment } from "./components/SelectPayment";
import { EnterEmail } from "./components/EnterEmail";
import { Payment } from "./components/Payment";
import { ArrowBack } from "@mui/icons-material";
import { PaymentMethod } from "./types";

interface IProps {
  details: IBreachDetails;
  open: boolean;
  onClose: () => void;
}

enum PaymentStep {
  SELECT_PAYMENT = "selectPayment",
  ENTER_EMAIL = "enterEmail",
  PAYMENT = "payment",
}

interface IState {
  currentStep: PaymentStep;
  paymentMethod: string;
  email: string;
}

enum PaymentAction {
  GO_BACK = "GO_BACK",
  GO_FORWARD = "GO_FORWARD",
  SELECT_PAYMENT = "SELECT_PAYMENT",
  SET_PAYMENT_METHOD = "SET_PAYMENT_METHOD",
  CONTINUE_TO_PAYMENT = "CONTINUE_TO_PAYMENT",
}

const initialState = {
  currentStep: PaymentStep.SELECT_PAYMENT,
  paymentMethod: "",
  email: "",
};

const navigationBar: Record<
  PaymentStep,
  { title: string; showBackButton: boolean }
> = {
  [PaymentStep.SELECT_PAYMENT]: {
    title: "Select your payment method",
    showBackButton: false,
  },
  [PaymentStep.ENTER_EMAIL]: {
    title: "Enter your email address",
    showBackButton: true,
  },
  [PaymentStep.PAYMENT]: {
    title: "Confirm your payment",
    showBackButton: true,
  },
};

const reducer = (state: any, action: any) => {
  const { currentStep, paymentMethod } = state;
  switch (action.type) {
    case PaymentAction.SELECT_PAYMENT:
      return { ...state, currentStep: action.payload };
    case PaymentAction.SET_PAYMENT_METHOD:
      return { ...state, paymentMethod: action.payload };
    case PaymentAction.GO_BACK:
      return {
        ...state,
        currentStep:
          currentStep === PaymentStep.ENTER_EMAIL
            ? PaymentStep.SELECT_PAYMENT
            : PaymentStep.ENTER_EMAIL,
      };
    case PaymentAction.GO_FORWARD:
      const payload = action.payload || state;
      return {
        ...state,
        ...payload,
        currentStep:
          currentStep === PaymentStep.SELECT_PAYMENT
            ? PaymentStep.ENTER_EMAIL
            : PaymentStep.PAYMENT,
      };
    case PaymentAction.CONTINUE_TO_PAYMENT:
      return {
        ...state,
        email: action.payload,
        currentStep: PaymentStep.PAYMENT,
      };
  }
};

export const PaymentFlow = ({ details, open, onClose }: IProps) => {
  const { isUserTermsChecked, handleUserTermsChange } = useUserTerms();
  const [currentStep, dispatch] = useReducer<Reducer<IState, any>>(
    reducer,
    initialState
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handlePaymentChange = (paymentMethod: string) => {
    dispatch({
      type: PaymentAction.SET_PAYMENT_METHOD,
      payload: paymentMethod,
    });
  };

  const handleGoBack = () => {
    dispatch({
      type: PaymentAction.GO_BACK,
    });
  };

  const handleNext = (email?: string | undefined) => {
    dispatch({
      type: PaymentAction.GO_FORWARD,
      payload:
        email && typeof email === "string" ? { email: email } : undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      keepMounted
      PaperProps={{
        sx: {
          position: isMobile ? "fixed" : "absolute",
          top: isMobile ? "50%" : "20%",
          left: "50%",
          transform: isMobile ? "translate(-50%, -50%)" : "translateX(-50%)",
          m: 0,
          borderRadius: isMobile
            ? theme.shape.borderRadius
            : theme.shape.borderRadius,
          maxHeight: isMobile ? "90vh" : "80vh",
          width: isMobile ? "95%" : "100%",
          padding: 0,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "16px 16px 0px 16px",
        }}
      >
        {navigationBar[currentStep.currentStep].showBackButton && (
          <Button
            variant="text"
            color="primary"
            sx={{ mb: 2, p: 0 }}
            onClick={handleGoBack}
          >
            <ArrowBack />
          </Button>
        )}
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          {navigationBar[currentStep.currentStep].title}
        </Typography>
      </Box>
      <DialogContent
        sx={{
          p: isMobile ? 2 : 3,
          overflow: "visible",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: 0,
        }}
      >
        <Box
          sx={{
            position: "relative",
            minHeight: "100px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            padding: "16px",
          }}
        >
          {currentStep.currentStep === PaymentStep.SELECT_PAYMENT && (
            <SelectPayment
              selectedPayment={currentStep.paymentMethod}
              handlePaymentChange={handlePaymentChange}
              isUserTermsChecked={isUserTermsChecked}
              handleUserTermsChange={handleUserTermsChange}
              handlePaymentNext={handleNext}
            />
          )}
          {currentStep.currentStep === PaymentStep.ENTER_EMAIL && (
            <EnterEmail email={currentStep.email} handleNext={handleNext} />
          )}

          {currentStep.currentStep === PaymentStep.PAYMENT && (
            <Payment
              breachDetails={details}
              email={currentStep.email}
              paymentMethod={currentStep.paymentMethod}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
